import { useDevTheme as useDevThemeContext } from '../config/DevThemeConfig';

/**
 * useDevTheme Hook
 *
 * ⚠️ THIS IS NOT A/B TESTING ⚠️
 *
 * Provides easy access to development theme configuration.
 * This is for testing alternative design themes during development,
 * NOT for production A/B testing.
 *
 * For production A/B testing, use:
 * - useABTest(experimentName) from '../contexts/ABTestContext'
 *
 * Usage:
 * const theme = useDevTheme();
 * <button className={theme.buttonPrimary}>Click me</button>
 *
 * Returns theme configuration object with:
 * - BrandIcon: Component - Brand icon component (Mountain or Compass)
 * - categoryIcons: Object - Category icon mapping
 * - primary: Object - Tailwind color classes (50-900)
 * - buttonPrimary: string - Primary button classes
 * - buttonSecondary: string - Secondary button classes
 * - textPrimary: string - Primary text color
 * - ... (see DevThemeConfig.jsx for full list)
 *
 * Location: apps/web/src/hooks/useDevTheme.js
 * Created: 2025-10-31
 * Part of: MVP 1.10 - Code migration (original useTheme.js)
 */
export const useDevTheme = () => {
  const { theme } = useDevThemeContext();
  return theme;
};

/**
 * useThemedClass Hook
 *
 * Helper to conditionally apply themed classes with additional utility classes.
 *
 * Usage:
 * const getClass = useThemedClass();
 * <button className={getClass('buttonPrimary', 'px-4 py-2 rounded')}>
 *   Click me
 * </button>
 *
 * @returns {Function} - Function that combines themed class with additional classes
 */
export const useThemedClass = () => {
  const { theme } = useDevThemeContext();

  return (themeKey, additionalClasses = '') => {
    const themedClass = theme[themeKey] || '';
    return `${themedClass} ${additionalClasses}`.trim();
  };
};
